import type { ComponentType } from "react";
import { EnvoyIcon, JobsIcon, OverviewIcon, QueueIcon, ResumeIcon } from "@/components/icons";
import { isJobsPath, ROUTES } from "@/lib/routes";

export type PlaceKey = "overview" | "jobs" | "applications" | "resume" | "envoy" | "pricing";

export interface Place {
    key: PlaceKey;
    label: string;
    to: string;
    icon: ComponentType<{ className?: string }>;
    /** In the header's nav; the envoy has its chip there, and its tab on a phone. */
    inHeader: boolean;
    /** On the phone's tab bar; the Overview is in the avatar menu there. */
    inTabBar: boolean;
}

/**
 * The places, in the order the header and the tab bar draw them, open to
 * everyone: a visitor sees the ones an account keeps with the example in
 * them (./previews), never a lock.
 */
export const PLACES: readonly Place[] = [
    {
        key: "jobs",
        label: "Jobs",
        to: ROUTES.HOME,
        icon: JobsIcon,
        inHeader: true,
        inTabBar: true,
    },
    {
        key: "overview",
        label: "Overview",
        to: ROUTES.OVERVIEW,
        icon: OverviewIcon,
        inHeader: true,
        inTabBar: false,
    },
    {
        key: "applications",
        label: "Applications",
        to: ROUTES.APPLICATIONS,
        icon: QueueIcon,
        inHeader: true,
        inTabBar: true,
    },
    {
        key: "resume",
        label: "Resume",
        to: ROUTES.RESUME,
        icon: ResumeIcon,
        inHeader: true,
        inTabBar: true,
    },
    {
        key: "envoy",
        label: "Envoy",
        to: ROUTES.ENVOY,
        icon: EnvoyIcon,
        inHeader: false,
        inTabBar: true,
    },
];

/** Which place an address is in, for the indicator: a posting and Saved are in Jobs, History in Applications. */
export function placeAt(pathname: string): PlaceKey | null {
    // The router matches paths in any case and with a trailing slash; so does the indicator.
    const lower = pathname.toLowerCase();
    const path = lower.length > 1 ? lower.replace(/\/+$/, "") || "/" : lower;
    if (path === ROUTES.OVERVIEW) return "overview";
    if (isJobsPath(path) || path.startsWith(`${ROUTES.JOBS}/`)) return "jobs";
    if ([ROUTES.APPLICATIONS, ROUTES.QUEUE, ROUTES.HISTORY].some((entry) => entry === path))
        return "applications";
    if (path === ROUTES.RESUME || path.startsWith(`${ROUTES.RESUME}/`)) return "resume";
    if (path === ROUTES.ENVOY) return "envoy";
    if (path === ROUTES.PRICING) return "pricing";
    return null;
}
